import React, { useState, useEffect } from "react";
import { RxReload } from "react-icons/rx";
import { useMyContext } from "../../components/Provider/CommonProvider";
import AdminNavbar from "../../components/CommonComponents/AdminNavbar";
import AdminTable from "../../components/CommonComponents/AdminTable";
import AdminTooltip from "../../components/CommonComponents/AdminTooltip";
import Response_Processing from "../../layout/Others/Response_Processing";
import moment from "moment";
import ExportIcon from "../../components/Images/icons/Export_Icon_01.png";
import { donwloadCSV, videodownloadPDF, videodownloadXLSX } from "../../components/CommonMethod/CommonMethods";

function DonationTransactions() {
    const BaseUrl = process.env.REACT_APP_Baseurl;
    const listApi = `${BaseUrl}/admin/donations/transactions`;
    const { fetchRequest } = useMyContext();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState({});
    const [searchTitle, setSearchTitle] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [withOutRefresh, setWithoutRefresh] = useState(false);
    const [itemToDelete, setItemToDelete] = useState([]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTitle);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTitle]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetchRequest({
                method: 'GET',
                url: listApi,
                params: {
                    page,
                    limit,
                    search: debouncedSearch
                }
            });

            if (response?.status) {
                setApiData(response.data.donations);
                setTotal(response.data.pagination);
            }
        } catch (error) {
            console.error('Failed to fetch donation transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, limit, debouncedSearch, withOutRefresh]);

    const fetchAllData = async (exportType, findScope) => {
        try {
            const response = await fetchRequest({
                method: 'GET',
                url: listApi,
                params: {
                    search: findScope === "ALL" ? null : debouncedSearch,
                    page: 1,
                    limit: findScope === "ALL" ? (total?.totalItems || 1000) : total?.totalItems,
                }
            });

            if (!response?.status) return;

            let exportList = response.data.donations;

            if (findScope !== "ALL") {
                if (itemToDelete.length === 0) {
                    alert("Please select at least one row to export.");
                    return;
                }
                exportList = exportList.filter(item => itemToDelete.includes(item.id));
            }

            const exportData = exportList.map((item, index) => ({
                "S.NO": index + 1,
                "EMAIL": item.email,
                "AMOUNT": item.amount,
                "CURRENCY": item.currency,
                "FREQUENCY": item.frequency,
                "METHOD": item.paymentMethod,
                "TRANSACTION ID": item.transactionId,
                "DATE": moment(item.createdAt).format("DD-MM-YYYY")
            }));

            const filename = "DONATION-TRANSACTIONS";
            const columns = ["S.NO", "EMAIL", "AMOUNT", "CURRENCY", "FREQUENCY", "METHOD", "TRANSACTION ID", "DATE"];

            if (exportType === "CSV") {
                await donwloadCSV(exportData, filename);
            } else if (exportType === "PDF") {
                await videodownloadPDF(exportData, filename, columns);
            } else if (exportType === "XLSX") {
                await videodownloadXLSX(exportData, filename, columns);
            }
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const handlePageChange = (page) => {
        setPage(page);
    };

    const handleSizePerPageChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    const reloadAction = () => {
        setWithoutRefresh(prev => !prev);
        setItemToDelete([]);
    };

    const handleRowSelect = (rowId) => {
        if (itemToDelete.includes(rowId)) {
            setItemToDelete(itemToDelete.filter(id => id !== rowId));
        } else {
            setItemToDelete(prev => [...prev, rowId]);
        }
    };

    const handleSelectAll = () => {
        if (itemToDelete.length === apiData.length) {
            setItemToDelete([]);
        } else {
            setItemToDelete(apiData.map(row => row.id));
        }
    };

    const columns = [
        {
            dataField: "userName",
            text: "User",
            sort: true,
            width: "200px",
            formatter: (cell, row) => (
                <div className="d-flex flex-column gap-1">
                    <p className="userNameProfile text-break mb-0">{cell}</p>
                    <span className="userDateText text-muted">{row.email}</span>
                </div>
            )
        },
        {
            dataField: "amount",
            text: "Amount",
            sort: true,
            width: "100px",
        },
        {
            dataField: "currency",
            text: "Currency",
            sort: true,
            width: "100px",
        },
        {
            dataField: "frequency",
            text: "Frequency",
            sort: true,
            width: "120px",
            formatter: (cell) => <span className="text-capitalize">{cell}</span>
        },
        {
            dataField: "paymentMethod",
            text: "Method",
            sort: true,
            width: "100px",
            formatter: (cell) => <span className="text-uppercase">{cell}</span>
        },
        {
            dataField: "transactionId",
            text: "Transaction ID",
            sort: true,
            width: "250px",
            formatter: (cell) => <span className="text-break" style={{ wordBreak: 'break-all' }}>{cell}</span>
        },
        {
            dataField: "createdAt",
            text: "Date",
            sort: true,
            width: "120px",
            formatter: (cell) => moment(cell).format("DD-MM-YYYY")
        }
    ];

    const options = {
        pageStartIndex: 1,
        sizePerPage: 10,
        sizePerPageList: [
            { text: '5', value: 5 },
            { text: '10', value: 10 },
            { text: '25', value: 25 },
            { text: 'All', value: total?.totalItems || 100 }
        ]
    };

    const selectRow = {
        mode: 'checkbox',
        selected: itemToDelete,
    };

    return (
        <>
            {loading && page === 1 && <Response_Processing />}
            <AdminNavbar heading={"Donation Transactions"} totalDataLength={total?.totalItems} navText={""} hideAddButton={true} />

            <div className='theme-bg-color p-3 rounded-3 mb-5'>
                <div className='d-flex align-items-center flex-wrap justify-content-between mb-3'>
                    <input
                        type='search'
                        placeholder='Search transactions...'
                        className='rounded-3 theme-border-color border-solid-width border-solid table-search theme-bg-color-secondary'
                        value={searchTitle}
                        onChange={(e) => setSearchTitle(e.target.value)}
                    />
                    <div className='d-flex align-items-center'>
                        <button
                            className={`bg-transparent reloadButton custom-tool-tip-parent theme-text-color p-2 me-2 primary border border-2 rounded-circle theme-border-color`}
                            onClick={reloadAction}
                        >
                            <RxReload style={{ width: "18px", height: "15px" }} className={`${loading ? "rotate-load" : ""}`} />
                            <AdminTooltip text="Refresh" top={"-41px"} className="d-none custom-tool-tip" />
                        </button>

                        <div className="dropdown d-block ms-2">
                            <button className="btn btn-success dropdown-toggle d-flex hideArrow" type="button" id="exportButton" data-bs-toggle="dropdown" aria-expanded="false">
                                <span className="fw-bolder text-white">Export</span>
                                <img src={ExportIcon} className="ms-2 exportIcon" alt="exportIcon" />
                            </button>
                            <ul className="dropdown-menu theme-bg-color" aria-labelledby="exportButton">
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("CSV", "SELECTED")}>CSV</li>
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("PDF", "SELECTED")}>PDF</li>
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("XLSX", "SELECTED")}>XLSX</li>
                            </ul>
                        </div>

                        <div className="dropdown d-block ms-2">
                            <button className="btn btn-success dropdown-toggle d-flex hideArrow" type="button" id="exportAllButton" data-bs-toggle="dropdown" aria-expanded="false">
                                <span className="fw-bolder text-white">Export All</span>
                                <img src={ExportIcon} className="ms-2 exportIcon" alt="exportIcon" />
                            </button>
                            <ul className="dropdown-menu theme-bg-color" aria-labelledby="exportAllButton">
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("CSV", "ALL")}>CSV</li>
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("PDF", "ALL")}>PDF</li>
                                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchAllData("XLSX", "ALL")}>XLSX</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <AdminTable
                    viewType="list"
                    data={apiData}
                    loading={loading}
                    options={options}
                    handlePageChange={handlePageChange}
                    page={page}
                    pagination={total}
                    handleSizePerPageChange={handleSizePerPageChange}
                    columns={columns}
                    limit={limit}
                    multiSelect={true}
                    selectRow={selectRow}
                    onRowSelect={handleRowSelect}
                    onSelectAll={handleSelectAll}
                />
            </div>
        </>
    );
}

export default DonationTransactions;
